import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice.ts';
import { updateGridDimensionConfig, updateGridPositionConfig } from '../grid/reduxStore/editorSlice.ts';
import { updateBlockDimensionsViaSocket, updateBlockPositionViaSocket } from '../grid/reduxStore/saveHandlers.ts';
import type { DimensionPositionStateChangedType } from '../hooks/UseBlockDimensionChangedHandler.ts';
import type { BlockCommandDependencies } from './BlockBaseCommand.ts';
import { BlockBaseCommand } from './BlockBaseCommand.ts';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents.tsx';
import { TableContentCommand } from './TableContentCommand.ts';
import type { TableType } from '../grid/reduxStore/table.types.ts';

/**
 * Resize block command
 */
export class UpdateBlockDimensionCommand extends BlockBaseCommand {
  readonly sectionId: string;

  private readonly blockId: string;
  private readonly previousDimension: DimensionPositionStateChangedType;
  private readonly newDimension: DimensionPositionStateChangedType;
  private tableContentCommand?: TableContentCommand;

  constructor(
    blockId: string,
    sectionId: string,
    previousDimension: DimensionPositionStateChangedType,
    newDimension: DimensionPositionStateChangedType,
    dependencies: BlockCommandDependencies,
    tableContent?: {
      previousContent: TableType;
      newContent: TableType;
    }
  ) {
    super(dependencies);
    this.blockId = blockId;
    this.sectionId = sectionId;
    this.previousDimension = { ...previousDimension };
    this.newDimension = { ...newDimension };

    if (tableContent) {
      this.tableContentCommand = new TableContentCommand(dependencies, {
        blockId,
        sectionId,
        previousContent: tableContent.previousContent,
        newContent: tableContent.newContent,
      });
    }
  }

  async execute(): Promise<boolean> {
    const dimensionSuccess = await this.updateDimension(this.newDimension);

    if (dimensionSuccess && this.tableContentCommand) {
      this.tableContentCommand.applyOptimistic();
      await this.tableContentCommand.persistToSocket();
    }

    return dimensionSuccess;
  }

  async undo(): Promise<boolean> {
    const dimensionSuccess = await this.updateDimension(this.previousDimension);

    if (dimensionSuccess && this.tableContentCommand) {
      await this.tableContentCommand.undo();
    }

    return dimensionSuccess;
  }

  async redo(): Promise<boolean> {
    const dimensionSuccess = await this.updateDimension(this.newDimension);

    if (dimensionSuccess && this.tableContentCommand) {
      await this.tableContentCommand.redo();
    }

    return dimensionSuccess;
  }

  private async updateDimension(dimensions: DimensionPositionStateChangedType): Promise<boolean> {
    try {
      this.setSaving();

      const positionChanged =
        this.previousDimension.xAxisPx !== this.newDimension.xAxisPx || this.previousDimension.yAxisPx !== this.newDimension.yAxisPx;

      if (positionChanged) {
        this.dispatch(
          updateGridPositionConfig({
            sectionId: this.sectionId,
            blockId: this.blockId,
            x: dimensions.xAxisPx,
            y: dimensions.yAxisPx,
          })
        );
      }

      this.dispatch(
        updateGridDimensionConfig({
          sectionId: this.sectionId,
          blockId: this.blockId,
          width: dimensions.widthPx,
          height: dimensions.heightPx,
        })
      );

      const dimensionResponse = await updateBlockDimensionsViaSocket(this.socketClient, {
        blockId: this.blockId,
        userId: this.userId,
        dimensions: {
          width_px: dimensions.widthPx,
          height_px: dimensions.heightPx,
        },
      });

      if (this.checkDocumentLockStatus(dimensionResponse.errorCode)) {
        this.rollback();
        return false;
      }

      if (dimensionResponse.status !== AcknowledgmentResponseStatus.OK) {
        console.error('Dimension update failed:', dimensionResponse);
        this.rollback();
        return false;
      }

      if (positionChanged) {
        const positionResponse = await updateBlockPositionViaSocket(this.socketClient, {
          blockId: this.blockId,
          userId: this.userId,
          position: {
            left_px: dimensions.xAxisPx,
            top_px: dimensions.yAxisPx,
          },
        });

        if (this.checkDocumentLockStatus(positionResponse.errorCode)) {
          this.rollback();
          return false;
        }

        if (positionResponse.status !== AcknowledgmentResponseStatus.OK) {
          console.error('Position update failed:', positionResponse);
          this.rollback();
          return false;
        }

        this.dispatch(setSaveStatusBasedOnApiResponse(positionResponse));
      } else {
        this.dispatch(setSaveStatusBasedOnApiResponse(dimensionResponse));
      }

      return true;
    } catch (error) {
      this.rollback();
      return false;
    }
  }

  private rollback() {
    this.dispatch(
      updateGridDimensionConfig({
        sectionId: this.sectionId,
        blockId: this.blockId,
        width: this.previousDimension.widthPx,
        height: this.previousDimension.heightPx,
      })
    );
    this.dispatch(
      updateGridPositionConfig({
        sectionId: this.sectionId,
        blockId: this.blockId,
        x: this.previousDimension.xAxisPx,
        y: this.previousDimension.yAxisPx,
      })
    );
    this.setNotSaved();
  }

  getCurrentBlockId(): string {
    return this.blockId;
  }
}
